import { type FC, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useEstateActivityLog } from "@/shared/hooks/useEstateActivityLog";
import { useDocuments } from "@/shared/hooks/useDocumentStorage";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { formatRelativeTime } from "@/shared/utils/formatters";
import { getActivityIcon, renderActivityRow } from "@/shared/utils/activityLogDisplay";

import type { ActivityLogEntry } from "@/shared/types/activityLog";

interface ViewingDocument {
  id: number;
  name: string;
  createdAt: string;
}

export const ActivitySection: FC = () => {
  const navigate = useNavigate();
  const person = usePerson();
  const estateId = person?.estate_id;
  const { data, isLoading } = useEstateActivityLog(estateId);
  const { data: documentsData } = useDocuments({ estateId });
  const [viewingDoc, setViewingDoc] = useState<ViewingDocument | null>(null);

  const existingDocumentIds = useMemo(
    () => new Set(documentsData?.documents.map((doc) => String(doc.id)) ?? []),
    [documentsData],
  );

  const entries = useMemo(
    () => (data?.pages.flatMap((page) => page.entries) ?? []).slice(0, 5),
    [data],
  );

  const handleDocumentClick = (entry: ActivityLogEntry) => {
    const name =
      (typeof entry.metadata.display_name === "string" && entry.metadata.display_name) ||
      (typeof entry.metadata.file_name === "string" && entry.metadata.file_name) ||
      "Document";
    setViewingDoc({ id: Number(entry.entity_id), name, createdAt: entry.created_at });
  };

  const handleEstateItemClick = (entry: ActivityLogEntry) =>
    navigate(`${POST_LOSS_ROUTES.ESTATE_INVENTORY}?item=${entry.entity_id}`);

  if (isLoading || entries.length === 0) return null;

  return (
    <div>
      <p className="font-semibold text-base tracking-[-0.32px] mb-4">Activity</p>
      <div className="flex flex-col rounded-xl border border-primary bg-white px-6 py-2">
        {entries.map((entry, index) => {
          const { Icon, bg, fg } = getActivityIcon(entry);
          const { title } = renderActivityRow(entry, {
            onDocumentClick:
              entry.entity_id && existingDocumentIds.has(entry.entity_id)
                ? handleDocumentClick
                : undefined,
            onEstateItemClick: handleEstateItemClick,
          });

          return (
            <div
              key={entry.id}
              className={`flex items-center gap-3 py-4 ${index > 0 ? "border-t border-primary" : ""}`}
            >
              <div
                className={`size-8 rounded-full ${bg} flex items-center justify-center shrink-0`}
              >
                <Icon className={`size-4 ${fg}`} aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-secondary tracking-[-0.02em] truncate">{title}</p>
              </div>
              <span className="text-xs text-tertiary tracking-[-0.02em] shrink-0">
                {formatRelativeTime(entry.created_at)}
              </span>
            </div>
          );
        })}
      </div>

      {viewingDoc && (
        <DocumentSheet
          isOpen
          setIsOpen={(open) => {
            if (!open) setViewingDoc(null);
          }}
          documentId={viewingDoc.id}
          documentName={viewingDoc.name}
          documentCreatedAt={new Date(viewingDoc.createdAt)}
          estateId={estateId}
        />
      )}
    </div>
  );
};
